import { useCallback, useRef, useState } from "react";

import type { NativeQuote } from "@/modules/dashboard/dispersions/services/paymentsQuotes.service";
import { classifyQuoteConflict } from "@/modules/dashboard/dispersions/utils/quoteConflict";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { normalizePaymentError } from "@/shared/services/payments/paymentErrors";

import { newIdempotencyKey, type NativeSwap, SwapService } from "../services/swap.service";

export type SwapPhase = "idle" | "processing" | "success" | "error";

/**
 * `consumed`: the quote was spent, so the conversion may have gone through on an attempt whose answer was
 * lost — never offer to retry it. `unconfirmed`: the quote could not even be read back; same caution.
 */
export type SwapErrorKind = "generic" | "consumed" | "unconfirmed";

/** The conversion is instant, but the money on its way stays on screen long enough to be seen. */
const MIN_TRANSIT_MS = 1400;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface UseWalletSwapSubmitOptions {
  /** The price lapsed before the conversion: nothing moved, a fresh quote is needed. */
  onQuoteExpired: () => void;
  /** The backend claimed or refused the quote: the next attempt needs a new one. */
  onQuoteSpent: () => void;
}

/**
 * Redeems a swap quote. One idempotency key per quote, reused by a retry of the same attempt, so a lost
 * answer never converts twice; a 409 is read back from `GET /quotes/:id` instead of guessed.
 */
export function useWalletSwapSubmit({ onQuoteExpired, onQuoteSpent }: UseWalletSwapSubmitOptions) {
  const [phase, setPhase] = useState<SwapPhase>("idle");
  const [swap, setSwap] = useState<NativeSwap | null>(null);
  const [errorKind, setErrorKind] = useState<SwapErrorKind>("generic");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);
  const keyQuoteRef = useRef<string | null>(null);

  const submit = async (quote: NativeQuote): Promise<NativeSwap | null> => {
    setPhase("processing");
    setErrorMessage(null);
    setErrorKind("generic");
    if (Date.parse(quote.expires_at) <= Date.now()) {
      keyRef.current = null;
      setPhase("idle");
      onQuoteExpired();
      return null;
    }
    if (!keyRef.current || keyQuoteRef.current !== quote.id) keyRef.current = newIdempotencyKey();
    keyQuoteRef.current = quote.id;

    const started = Date.now();
    try {
      const { data } = await SwapService.createSwap({ quote: quote.id }, keyRef.current);
      await wait(Math.max(0, MIN_TRANSIT_MS - (Date.now() - started)));
      keyRef.current = null;
      setSwap(data.swap);
      setPhase("success");
      return data.swap;
    } catch (error: unknown) {
      const normalized = normalizePaymentError(error, { quoteExpiresAt: quote.expires_at });
      if (normalized.kind === "quote_expired") {
        keyRef.current = null;
        setPhase("idle");
        onQuoteExpired();
        return null;
      }
      let kind: SwapErrorKind = "generic";
      if (HttpApiService.isConflictError(error)) {
        const verdict = await classifyQuoteConflict(quote.id, undefined);
        kind =
          verdict === "quote_consumed" ? "consumed" : verdict === "unconfirmed" ? "unconfirmed" : "generic";
      }
      // Any answer from the server settles this attempt: the next one is a new operation on a new quote.
      // With no answer the conversion may still be in flight, so the key is kept for a retry of this one.
      if (HttpApiService.isAxiosError(error) && error.response) {
        keyRef.current = null;
        onQuoteSpent();
      }
      setErrorKind(kind);
      setErrorMessage(normalized.messages.join(" ") || null);
      setPhase("error");
      return null;
    }
  };

  const reset = useCallback(() => {
    keyRef.current = null;
    setPhase("idle");
    setSwap(null);
    setErrorMessage(null);
    setErrorKind("generic");
  }, []);

  return { phase, setPhase, swap, errorKind, errorMessage, submit, reset };
}
